/**
 * Bin → `heatmap-weight` normalization.
 *
 * The analytics bins span five orders of magnitude (Night City kills: median 58,
 * peak 259,269). Normalizing linearly against `peak_cell` pushes ~98% of bins into a
 * single narrow weight band, so the render becomes a map of *where bins exist* rather
 * than *how many events they hold* — a uniform saturated wash. Compressive scaling
 * (log / percentile clamp / rank) is what puts real signal back in the weight channel.
 *
 * Weights land on each feature as the `w` property; the heat layers read `['get', 'w']`.
 */

export type HeatmapWeightScale =
    /** log(count) capped at a percentile. Even spread — the sane default. */
    | 'log-clamp'
    /** log(count) over the full range. Softer top end than log-clamp. */
    | 'log'
    /** count/cap, clamped. High contrast; isolates peaks, flattens everything else. */
    | 'clamp'
    /** (count/peak)^exponent. Tunable midpoint. */
    | 'pow'
    /** Empirical CDF — every percentile gets equal colour budget. Max contrast. */
    | 'rank'
    /** count/peak, linear. Included only to demonstrate why it fails here. */
    | 'linear-peak';

export type HeatmapWeightConfig = {
    readonly scale: HeatmapWeightScale;
    /** Percentile (0–1) at which weight saturates to 1. Used by `log-clamp` / `clamp`. */
    readonly clampPercentile: number;
    /** Exponent for `pow`. Lower lifts the low end harder. */
    readonly powExponent: number;
    /**
     * Zero out the sparsest bins (0–1). The long tail is what paints city-wide fog:
     * on Night City the bottom 35% of kill bins hold well under 0.1% of all kills.
     */
    readonly hideBottomPercentile: number;
};

/**
 * Tuned on Night City via the Heatmap Lab (`?heatlab=1`).
 *
 * `clamp` beats `log-clamp` here for a second-order reason worth knowing: fixing the
 * weight *histogram* is necessary but not sufficient. Density accumulates spatially, and
 * these bins tile the whole city — so any curve that gives the *typical* bin real weight
 * re-saturates into one blob no matter how even the histogram looks. Definition needs the
 * median bin near zero and only the top few percent carrying weight.
 */
export const DEFAULT_HEATMAP_WEIGHT: HeatmapWeightConfig = {
    scale: 'clamp',
    clampPercentile: 0.95,
    powExponent: 0.35,
    hideBottomPercentile: 0.6,
};

export type HeatmapCategoryStats = {
    readonly eventType: string;
    readonly binCount: number;
    /** Ascending bin counts — backs percentile and rank lookups. */
    readonly sortedCounts: readonly number[];
    readonly peak: number;
    /** Saturation cap derived from `clampPercentile`. */
    readonly cap: number;
    /** Bins below this count get weight 0. */
    readonly floorCount: number;
};

function percentile(sortedCounts: readonly number[], p: number): number {
    if (sortedCounts.length === 0) return 1;
    const index = Math.min(sortedCounts.length - 1, Math.max(0, Math.floor(p * (sortedCounts.length - 1))));
    return sortedCounts[index] ?? 1;
}

/** Index of the first entry >= count, i.e. the empirical CDF position. */
function rankOf(sortedCounts: readonly number[], count: number): number {
    let lo = 0;
    let hi = sortedCounts.length;
    while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if ((sortedCounts[mid] ?? 0) < count) lo = mid + 1;
        else hi = mid;
    }
    return sortedCounts.length > 1 ? lo / (sortedCounts.length - 1) : 1;
}

function weigh({ count, stats, config }: { count: number; stats: HeatmapCategoryStats; config: HeatmapWeightConfig }) {
    if (count < stats.floorCount) return 0;

    switch (config.scale) {
        case 'log':
            return Math.log1p(count) / Math.log1p(stats.peak);
        case 'clamp':
            return Math.min(1, count / Math.max(1, stats.cap));
        case 'pow':
            return (count / stats.peak) ** config.powExponent;
        case 'rank':
            return rankOf(stats.sortedCounts, count);
        case 'linear-peak':
            return count / stats.peak;
        // 'log-clamp' falls through here.
        default:
            return Math.min(1, Math.log1p(count) / Math.log1p(Math.max(1, stats.cap)));
    }
}

/**
 * Compute per-category stats and stamp a normalized `w` (0–1) onto every `bin` feature.
 * Mutates `geojson` in place so callers can hand the same object back to `setData`.
 */
export function applyHeatmapWeights({
    geojson,
    config = DEFAULT_HEATMAP_WEIGHT,
}: {
    geojson: GeoJSON.FeatureCollection;
    config?: HeatmapWeightConfig;
}): Record<string, HeatmapCategoryStats> {
    const countsByType = new Map<string, number[]>();

    for (const feature of geojson.features) {
        const properties = feature.properties;
        if (!properties || properties.kind !== 'bin') continue;
        const eventType = String(properties.event_type);
        const events = Number(properties.events) || 0;
        const bucket = countsByType.get(eventType);
        if (bucket) bucket.push(events);
        else countsByType.set(eventType, [events]);
    }

    const statsByType: Record<string, HeatmapCategoryStats> = {};
    for (const [eventType, counts] of countsByType) {
        const sortedCounts = [...counts].sort((a, b) => a - b);
        statsByType[eventType] = {
            eventType,
            binCount: sortedCounts.length,
            sortedCounts,
            peak: Math.max(1, sortedCounts[sortedCounts.length - 1] ?? 1),
            cap: Math.max(1, percentile(sortedCounts, config.clampPercentile)),
            floorCount: config.hideBottomPercentile > 0 ? percentile(sortedCounts, config.hideBottomPercentile) : 0,
        };
    }

    for (const feature of geojson.features) {
        const properties = feature.properties;
        if (!properties) continue;
        if (properties.kind !== 'bin') {
            properties.w = 0;
            continue;
        }
        const stats = statsByType[String(properties.event_type)];
        if (!stats) {
            properties.w = 0;
            continue;
        }
        const w = weigh({ count: Number(properties.events) || 0, stats, config });
        properties.w = Number.isFinite(w) ? Math.max(0, Math.min(1, w)) : 0;
    }

    return statsByType;
}

/**
 * Percent of bins falling in each weight decile. An even spread means the weight
 * channel is carrying information; a single tall bar means it is not.
 * Used by the Heatmap Lab readout to make over-flattening visible while tuning.
 */
export function weightDecileHistogram({
    stats,
    config,
}: {
    stats: HeatmapCategoryStats;
    config: HeatmapWeightConfig;
}): number[] {
    const histogram = new Array<number>(10).fill(0);
    for (const count of stats.sortedCounts) {
        const w = Math.max(0, Math.min(1, weigh({ count, stats, config })));
        const bucket = Math.min(9, Math.floor(w * 10));
        histogram[bucket] = (histogram[bucket] ?? 0) + 1;
    }
    const total = stats.sortedCounts.length || 1;
    return histogram.map(n => Math.round((n / total) * 1000) / 10);
}
